import { database, recordKey, userMetaKey } from '../../lib/database';
import type { SyncEngine } from '../../lib/sync-engine';
import type { ClassroomSessionDocument, ClassroomKnowledgeBlock, ClassroomQuiz } from '../../types/sync';
import { classroomAI, outputLanguage, processingError, validateFull, type ClassroomAI, type FullSummary, type FullSummaryInput } from './classroom-ai';
import { pendingWindow, selectQuizBlocks, splitSourceText } from './classroom-processing-rules';
import { classroomSummaryNoteID, fullSummaryNote } from './classroom-summary-note';
import type { KnowledgeNoteDocument } from '../../types/note';

export interface ProcessingCheckpoint {
  revision?: number; sessionID: string; language: string; enabled: boolean; flush: boolean; finalize: boolean;
  phase: string; error?: string; paused?: boolean; summaryNoteID?: string;
  cached?: { key: string; value: unknown };
}
export interface ProcessingEngine { userId: string; update: SyncEngine['update']; upsert: SyncEngine['upsert']; fillEmptyNote: SyncEngine['fillEmptyNote'] }

/** 账号作用域的串行队列。阶段进度使用现有课堂字段，调度和付费响应只保存在本地检查点。 */
export class ClassroomProcessing {
  private controller = new AbortController();
  private timer?: ReturnType<typeof setInterval>;
  private working = false;
  private lifecycle = 0;
  private listeners = new Set<() => void>();
  private states = new Map<string, ProcessingCheckpoint>();
  constructor(private engine: ProcessingEngine, private ai: ClassroomAI = classroomAI) {}
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  state = (id: string) => this.states.get(id);
  private key(id: string) { return userMetaKey(this.engine.userId, `classroom-processing:${id}`); }
  private notify() { this.listeners.forEach(listener => listener()); }
  async start() {
    const lifecycle = ++this.lifecycle;
    this.controller.abort();
    if (this.timer) clearInterval(this.timer);
    while (this.working) await new Promise(resolve => setTimeout(resolve, 10));
    if (lifecycle !== this.lifecycle) return;
    this.controller = new AbortController();
    const entries = await database.meta.where('key').startsWith(this.key('')).toArray();
    if (this.controller.signal.aborted || lifecycle !== this.lifecycle) return;
    for (const entry of entries) { try { const state = JSON.parse(entry.value) as ProcessingCheckpoint; this.states.set(state.sessionID, state); } catch { /* 损坏的调度记录不触发模型调用。 */ } }
    this.notify(); this.timer = setInterval(() => { void this.run(); }, 3000); void this.run();
  }
  stop() { this.lifecycle += 1; this.controller.abort(); if (this.timer) clearInterval(this.timer); }
  async request(id: string, options: { flush?: boolean; finalize?: boolean; retry?: boolean } = {}) {
    const saved = await database.meta.get(this.key(id));
    const current = this.states.get(id)?.cached ? this.states.get(id) : saved ? JSON.parse(saved.value) as ProcessingCheckpoint : undefined;
    const state: ProcessingCheckpoint = { sessionID: id, language: outputLanguage(), enabled: true, flush: false, finalize: false, phase: '等待整理', ...current };
    state.revision = Math.max(Date.now(), (this.states.get(id)?.revision ?? 0) + 1); state.enabled = true;
    state.flush ||= Boolean(options.flush); state.finalize ||= Boolean(options.finalize);
    if (options.retry) { state.error = undefined; state.paused = false; }
    await this.save(state); void this.run();
  }
  async pause(id: string, paused: boolean) {
    const current = this.states.get(id);
    if (current) { await this.save({ ...current, paused, revision: Math.max(Date.now(), (current.revision ?? 0) + 1) }); if (!paused) void this.run(); }
  }
  private async save(state: ProcessingCheckpoint) {
    const requested = this.states.get(state.sessionID);
    if ((requested?.revision ?? 0) > (state.revision ?? 0)) {
      state.revision = requested!.revision; state.flush ||= requested!.flush;
      state.finalize ||= requested!.finalize; state.paused = requested!.paused; state.enabled = requested!.enabled;
    }
    await database.meta.put({ key: this.key(state.sessionID), value: JSON.stringify(state) });
    this.states.set(state.sessionID, { ...state }); this.notify();
  }
  private async read(id: string) {
    const record = await database.records.get(recordKey(this.engine.userId, 'classroom.session', id));
    if (!record || record.deleted) return;
    return record.payload as ClassroomSessionDocument;
  }
  private async mutate(id: string, transform: (session: ClassroomSessionDocument) => ClassroomSessionDocument) {
    this.controller.signal.throwIfAborted();
    await this.engine.update('classroom.session', id, payload => transform(payload as ClassroomSessionDocument));
  }
  private async block(id: string, blockID: string, patch: Partial<ClassroomKnowledgeBlock>, finishSegments = false) {
    await this.mutate(id, session => {
      if (!session.knowledgeBlocks.some(block => block.id === blockID)) throw new Error('来源已更新');
      return { ...session, knowledgeBlocks: session.knowledgeBlocks.map(block => block.id === blockID ? { ...block, ...patch } : block), transcriptSegments: finishSegments ? session.transcriptSegments.map(segment => segment.breakpointGroupID === blockID ? { ...segment, processingState: 'summarized' } : segment) : session.transcriptSegments };
    });
  }
  /** 响应先落本地再发布；同阶段保存失败重试时复用响应，不主动再次付费。 */
  private async response<T>(state: ProcessingCheckpoint, key: string, operation: () => Promise<T>): Promise<T> {
    if (state.cached?.key === key) return state.cached.value as T;
    const value = await operation();
    this.controller.signal.throwIfAborted();
    state.cached = { key, value };
    this.states.set(state.sessionID, state);
    await this.save(state);
    return value;
  }
  async run() {
    if (this.working || this.controller.signal.aborted || this.engine.userId === 'web-ui-preview-user') return;
    this.working = true;
    try {
      const work = async () => {
        for (const cachedState of this.states.values()) {
          if (cachedState.error || cachedState.paused || !cachedState.enabled) continue;
          const saved = await database.meta.get(this.key(cachedState.sessionID));
          const state = cachedState.cached ? cachedState : saved ? JSON.parse(saved.value) as ProcessingCheckpoint : cachedState;
          if (!state.enabled || state.error || state.paused) continue;
          try { await this.process(state); }
          catch (error) {
            if (this.controller.signal.aborted) return;
            state.error = processingError(error); state.phase = '需要重试'; await this.save(state);
          }
        }
      };
      if (navigator.locks) await navigator.locks.request(`classmind-processing:${this.engine.userId}`, { ifAvailable: true }, async lock => { if (lock) await work(); });
      else {
        // 无浏览器互斥锁时明确暂停，避免多个标签页重复调用付费服务。
        for (const state of this.states.values()) if (state.enabled && !state.error) { state.error = '此浏览器缺少后台任务互斥能力，请使用支持 Web Locks 的浏览器。'; await this.save(state); }
      }
    } catch {
      // 本地存储不可用时停止自动调度，保留内存中的已返回结果供显式重试。
      for (const state of this.states.values()) if (state.enabled) {
        state.error = '本地保存未完成，请检查浏览器存储空间后重试。'; state.phase = '需要重试';
      }
      this.notify();
    } finally { this.working = false; }
  }
  private async process(state: ProcessingCheckpoint) {
    let session = await this.read(state.sessionID);
    if (!session) { state.enabled = false; await this.save(state); return; }
    const id = session.id, signal = this.controller.signal;
    while (true) {
      signal.throwIfAborted();
      session = await this.read(id); if (!session) return;
      if (session.transcriptSegments.some(segment => segment.isFinalized && segment.processingState === 'pending' && !segment.text.trim() && segment.speakerLabel !== 'source:photo:processing')) {
        await this.mutate(id, current => ({ ...current, transcriptSegments: current.transcriptSegments.map(segment => segment.isFinalized && segment.processingState === 'pending' && !segment.text.trim() && segment.speakerLabel !== 'source:photo:processing' ? { ...segment, processingState: 'summarized' } : segment) }));
        continue;
      }
      const oversized = session.transcriptSegments.filter(segment => segment.isFinalized && segment.processingState === 'pending' && segment.speakerLabel !== 'source:photo:processing' && splitSourceText(segment.text, session!.transcriptLanguage).length > 1);
      if (oversized.length) {
        const ids = new Set(oversized.map(segment => segment.id));
        await this.mutate(id, current => ({ ...current, transcriptSegments: current.transcriptSegments.flatMap(segment => {
          if (!ids.has(segment.id) || segment.processingState !== 'pending') return [segment];
          const parts = splitSourceText(segment.text, current.transcriptLanguage);
          return parts.map((text, index) => ({ ...segment, id: index === 0 ? segment.id : crypto.randomUUID(), text,
            startTime: segment.startTime + (segment.endTime - segment.startTime) * index / parts.length,
            endTime: segment.startTime + (segment.endTime - segment.startTime) * (index + 1) / parts.length }));
        }) }));
        continue;
      }
      const latest = this.states.get(id);
      if (latest?.paused) { state.paused = true; return; }
      state.flush ||= Boolean(latest?.flush); state.finalize ||= Boolean(latest?.finalize);
      let block = session.knowledgeBlocks.find(item => ['polishing', 'summarizing', 'failed'].includes(item.processingStatus));
      if (!block) {
        const segments = pendingWindow(session, state.flush || state.finalize || Boolean(session.pausedAt));
        if (!segments.length) break;
        const ids = new Set(segments.map(segment => segment.id));
        block = { id: crypto.randomUUID(), title: '正在整理', summary: '', bulletPoints: [], keywords: [], sourceText: segments.map(segment => segment.text).join('\n'), polishedTranscript: '', startTime: segments[0]!.startTime, endTime: segments.at(-1)!.endTime, createdAt: new Date().toISOString(), orderIndex: Math.max(-1, ...session.knowledgeBlocks.map(item => item.orderIndex)) + 1, processingStatus: 'polishing', retryCount: 0 };
        const claimed = block;
        await this.mutate(id, current => {
          if (current.transcriptSegments.filter(segment => ids.has(segment.id) && segment.processingState === 'pending').length !== ids.size) throw new Error('来源已更新');
          return { ...current, knowledgeBlocks: [...current.knowledgeBlocks, claimed], transcriptSegments: current.transcriptSegments.map(segment => ids.has(segment.id) ? { ...segment, processingState: 'claimed', breakpointGroupID: claimed.id } : segment) };
        });
      }
      try {
        if (!block.polishedTranscript) {
          state.phase = '正在重整原文'; await this.save(state);
          const text = await this.response(state, `polish:${block.id}`, () => this.ai.polish(block!.sourceText, state.language, signal));
          await this.block(id, block.id, { polishedTranscript: text, processingStatus: 'summarizing', lastError: null });
          block = { ...block, polishedTranscript: text }; state.cached = undefined; await this.save(state);
        }
        state.phase = '正在生成分段总结'; await this.save(state);
        const result = await this.response(state, `summary:${block.id}`, () => this.ai.summarize(block!.polishedTranscript, state.language, signal));
        await this.block(id, block.id, { title: result.title, summary: result.summary, bulletPoints: result.bulletPoints, keywords: result.keywords, processingStatus: 'ready', lastError: null }, true);
        state.cached = undefined; await this.save(state);
      } catch (error) {
        if (!signal.aborted) await this.block(id, block.id, { processingStatus: 'failed', lastError: processingError(error), retryCount: block.retryCount + 1 });
        throw error;
      }
    }
    session = await this.read(id); if (!session) return;
    // 图片尚在解析时不得把不完整来源发布成整堂课总结。
    if (state.finalize && session.transcriptSegments.some(segment => segment.processingState !== 'summarized')) { state.phase = '等待来源处理完成'; await this.save(state); return; }
    if (state.finalize && !state.summaryNoteID) await this.finalize(state, session);
    while (true) {
      session = await this.read(id); if (!session) return;
      const batch = selectQuizBlocks(session.knowledgeBlocks, session.transcriptLanguage, state.finalize);
      if (batch.skipped.length) await this.mutate(id, current => ({ ...current, knowledgeBlocks: current.knowledgeBlocks.map(block => batch.skipped.includes(block.id) ? { ...block, processingStatus: 'done' } : block) }));
      if (!batch.selected.length) break;
      state.phase = '正在生成课堂练习'; await this.save(state);
      const blockIDs = batch.selected.map(block => block.id), ownerID = blockIDs.at(-1)!;
      await this.mutate(id, current => ({ ...current, knowledgeBlocks: current.knowledgeBlocks.map(block => blockIDs.includes(block.id) ? { ...block, processingStatus: 'quizPending' } : block) }));
      try {
        const result = await this.response(state, `quiz:${blockIDs.join(',')}`, () => this.ai.quiz({ blockTitle: session!.title, blockSummary: batch.selected.map(block => block.sourceText).join('\n'), bulletPoints: batch.selected.flatMap(block => block.bulletPoints), keywords: [...new Set(batch.selected.flatMap(block => block.keywords))], outputLanguage: state.language }, signal));
        if (!Array.isArray(result.questions) || result.questions.some(question => question.type !== 'singleChoice' || !question.stem || !Array.isArray(question.options) || question.options.length !== 4 || !question.correctAnswer)) throw new Error('练习结果无效');
        const quiz: ClassroomQuiz = { id: ownerID, title: result.title, createdAt: new Date().toISOString(), difficulty: result.difficulty, isCompleted: false, knowledgeBlockID: ownerID, questions: result.questions.map((question, orderIndex) => ({ ...question, id: crypto.randomUUID(), userAnswer: '', orderIndex })) };
        await this.mutate(id, current => ({ ...current, quizzes: result.questions.length && !current.quizzes.some(item => item.id === quiz.id) ? [...current.quizzes, quiz] : current.quizzes, knowledgeBlocks: current.knowledgeBlocks.map(block => blockIDs.includes(block.id) ? { ...block, processingStatus: 'done', lastError: null } : block) }));
        state.cached = undefined; await this.save(state);
      } catch (error) {
        if (signal.aborted || state.cached) throw error;
        await this.mutate(id, current => ({ ...current, knowledgeBlocks: current.knowledgeBlocks.map(block => blockIDs.includes(block.id) ? { ...block, processingStatus: 'done', lastError: '本批练习未生成，原文与总结已保留。' } : block) }));
        state.cached = undefined;
      }
    }
    state.flush = false; state.phase = state.finalize ? (session.knowledgeBlocks.length ? '课堂整理完成' : '没有可总结内容') : '等待下一段内容';
    if (state.finalize) state.enabled = false;
    await this.save(state);
  }
  private async finalize(state: ProcessingCheckpoint, session: ClassroomSessionDocument) {
    if (!session.isFinished) return;
    if (!session.knowledgeBlocks.length) { state.phase = '没有可总结内容'; return; }
    const noteID = await classroomSummaryNoteID(session.id);
    const existing = await database.records.where('userId').equals(this.engine.userId).filter(record => record.entityType === 'knowledge.note' && record.entityId.toLowerCase() === noteID).first();
    if (existing?.deleted) throw new Error('总结笔记已删除');
    if (existing && !existing.deleted && (existing.payload as KnowledgeNoteDocument).blocks.some(block => block.text.trim())) {
      state.summaryNoteID = existing.entityId; return; // 现有总结及用户编辑是事实源，不覆盖。
    }
    const input: FullSummaryInput = { title: session.title, outputLanguage: state.language, sections: [...session.knowledgeBlocks].sort((a, b) => a.orderIndex - b.orderIndex).map(block => ({ id: block.id, title: block.title, summary: block.summary, bulletPoints: block.bulletPoints, sourceText: block.polishedTranscript || block.sourceText })) };
    if (input.sections.length > 1000 || input.sections.reduce((sum, section) => sum + section.title.length + section.summary.length + section.bulletPoints.join('').length + section.sourceText.length, 0) > 200_000) throw new Error('课堂来源超过完整总结上限');
    const fingerprint = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(input)))), byte => byte.toString(16).padStart(2, '0')).join('');
    state.phase = '正在生成完整课堂总结'; await this.save(state);
    const result = validateFull(await this.response<FullSummary>(state, `full:${fingerprint}`, () => this.ai.full(input, this.controller.signal)), input);
    const latest = await this.read(session.id);
    if (!latest || latest.transcriptSegments.some(segment => segment.processingState !== 'summarized') || latest.knowledgeBlocks.length !== session.knowledgeBlocks.length || latest.knowledgeBlocks.some(block => { const old = session.knowledgeBlocks.find(item => item.id === block.id); return !old || old.sourceText !== block.sourceText || old.polishedTranscript !== block.polishedTranscript || old.summary !== block.summary || old.title !== block.title || JSON.stringify(old.bulletPoints) !== JSON.stringify(block.bulletPoints); })) throw new Error('完整总结来源已改变');
    const note = await fullSummaryNote(latest, result, fingerprint);
    if (existing) note.id = existing.entityId;
    this.controller.signal.throwIfAborted();
    await this.engine.fillEmptyNote(note);
    await this.mutate(session.id, current => ({ ...current, summary: current.summary || result.overview }));
    state.summaryNoteID = note.id; state.cached = undefined; await this.save(state);
  }
}
